import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTypedSelector } from 'src/State/RootReducer'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Element, useShopSystemConfiguration, Components } from 'src/Libs/shop'
import { SYSTEM_CONFIGURE_PROGRESS_PAGE_PARAM } from 'src/Libs/shop/consts'
import { ProgressBarSteps } from 'src/Libs/shop/types'
import { useEffectShopAccess } from 'src/Libs/shop/hooks'
import { Product as SmartMeterProduct } from 'src/Libs/shop/models/product.model'
import { useShopChannelRules } from 'src/Libs/shop/hooks/use-shop-channel-type'
import useModals from 'src/Hooks/useModals'
import { addProduct, decreaseQuantity, increaseQuantity, removeProduct } from 'src/State/ShopSystemConfiguration/ShopSystemConfigurationAction'

interface SelectedConfiguredProduct {
  id: string
  qty: number
}

interface UseShopNormalAdditionalComponentReturn {
  title: string
  categories: Components[]
  isLoading: boolean
  onBack: () => void
  currentTransformers: Element[]
  availableSmartMeters: SmartMeterProduct[] | null
  showPowerMeterSection: boolean
  showAddPowerMeter: boolean
  powerMeterTabLabel: string
  powerMeterInitialId: number
  onMoreInfo: (productId: string) => void
  isProductSelected: (productId: string) => boolean
  getSelectedProduct: (productId: string) => SelectedConfiguredProduct | undefined
  onDecreaseProductQuantity: (categoryId: string, selectedProductId: string, selectedProductQty: number) => void
  onIncreaseProductQuantity: (categoryId: string, selectedProductId: string) => void
  onAddProductToCart: (categoryId: string, productId: string, minPurchase: number, productHint: string | false) => void
  onRemoveProductFromCart: (categoryId: string, productId: string) => void
}

/*
 * Handles data and product selection logic for the Normal Additional Component step
 */
export const useShopNormalAdditionalComponent = (): UseShopNormalAdditionalComponentReturn => {
  const { t } = useCustomTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { getSystemConfigurationComponents, getShopPowerMeters } = useShopSystemConfiguration()
  const { allowSmartMeter, allowPowerMeter } = useShopChannelRules()
  const { openShopMoreInfo, openShopHintModal } = useModals()

  const configuratorItems = useTypedSelector(state => state.systemStorage.items)
  const powerMeters = configuratorItems.length > 0 ? configuratorItems[0].configuration.powerMeters : null
  const selectedElement = configuratorItems.length > 0 ? configuratorItems[0].configuration.configuratorId : null
  const components = configuratorItems.length > 0 ? configuratorItems[0].configuration.components : null

  const [categories, setCategories] = useState<Components[]>([])
  const [currentTransformers, setCurrentTransformers] = useState<Element[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [availableSmartMeters, setAvailableSmartMeters] = useState<SmartMeterProduct[] | null>(null)
  const [showAddPowerMeter, setShowAddPowerMeter] = useState(false)

  const showSmartMeters = allowSmartMeter && Boolean(availableSmartMeters?.length)
  const showPowerMeterSection = showAddPowerMeter || showSmartMeters
  const powerMeterInitialId = showPowerMeterSection ? 1 : 0

  useEffect(() => {
    if (!selectedElement) {
      return navigate(location.pathname + `?${SYSTEM_CONFIGURE_PROGRESS_PAGE_PARAM}=${ProgressBarSteps.BasicSystem}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffectShopAccess(() => {
    setIsLoading(true)
    if (selectedElement) {
      getSystemConfigurationComponents(selectedElement)
        .then(result => {
          const filteredCategories =
            result?.components
              .filter(category => category.products.length > 0 && category.mandatory === false)
              .sort((a, b) => a.position - b.position) ?? []

          setCategories(filteredCategories)

          const hasAvailablePowerMeter = Boolean(result?.powerMeterExternal || result?.powerMeterFarming || result?.powerMeterHouseConnection)
          setShowAddPowerMeter(allowPowerMeter && hasAvailablePowerMeter)

          const smartMeters = result?.smartMeterTypes?.flatMap(smartMeterType => (smartMeterType.product ? [smartMeterType.product] : [])) ?? []
          setAvailableSmartMeters(allowSmartMeter ? smartMeters : null)
        })
        .finally(() => setIsLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffectShopAccess(async () => {
    setCurrentTransformers([])
    setIsLoading(true)

    const powerMetersArray = powerMeters?.reduce<string[]>((acc, item) => {
      if (item.powerMeterId !== null) {
        acc.push(item.powerMeterId)
      }
      if (item.powerConverterId && item.powerConverterId !== null) {
        acc.push(item.powerConverterId)
      }
      return acc
    }, [])

    powerMetersArray &&
      powerMetersArray.length > 0 &&
      (await getShopPowerMeters(powerMetersArray)
        .then(result => setCurrentTransformers(result ?? []))
        .catch(error => console.log(error)))

    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [powerMeters])

  const onBack = (): void => {
    navigate(location.pathname + `?${SYSTEM_CONFIGURE_PROGRESS_PAGE_PARAM}=${ProgressBarSteps.MainComponent}`)
  }

  const onMoreInfo = (productId: string): void => {
    openShopMoreInfo({ productId })
  }

  const getSelectedProduct = (productId: string): SelectedConfiguredProduct | undefined => {
    return components?.flatMap(component => component.products).find(selectedProduct => selectedProduct.id === productId)
  }

  const isProductSelected = (productId: string): boolean => {
    return Boolean(getSelectedProduct(productId))
  }

  const onDecreaseProductQuantity = (categoryId: string, selectedProductId: string, selectedProductQty: number): void => {
    if (selectedProductQty > 1) {
      selectedProductId && dispatch(decreaseQuantity(categoryId, selectedProductId))
    } else {
      selectedProductId && dispatch(removeProduct(categoryId, selectedProductId))
    }
  }

  const onIncreaseProductQuantity = (categoryId: string, selectedProductId: string): void => {
    selectedProductId && dispatch(increaseQuantity(categoryId, selectedProductId))
  }

  const onAddProductToCart = (categoryId: string, productId: string, minPurchase: number, productHint: string | false): void => {
    dispatch(addProduct(categoryId, productId, minPurchase))

    if (productHint) {
      openShopHintModal({ text: productHint })
    }
  }

  const onRemoveProductFromCart = (categoryId: string, productId: string): void => {
    dispatch(removeProduct(categoryId, productId))
  }

  return {
    title: t('shop.additional-component.title'),
    categories,
    isLoading,
    onBack,
    currentTransformers,
    availableSmartMeters,
    showPowerMeterSection,
    showAddPowerMeter,
    powerMeterTabLabel: t('shop.additional-component.power-meter-tab-label'),
    powerMeterInitialId,
    onMoreInfo,
    isProductSelected,
    getSelectedProduct,
    onDecreaseProductQuantity,
    onIncreaseProductQuantity,
    onAddProductToCart,
    onRemoveProductFromCart,
  }
}
